import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Active order on the backend
  currentOrderId: null,
  orderNumber: null,
  referenceNumber: null,
  status: null, // draft | on_hold | paid | cancelled

  // Customer attached to the order
  selectedCustomer: null, // { id, name, phone_number, credit_balance }

  // Salesperson attached to the order (order-level)
  selectedSalesperson: null, // { id, name }

  // Cart items — mirrors the backend order_items
  cartItems: [],
  /*
    Each cartItem:
    {
      order_item_id,     // backend id — needed for PATCH _destroy
      product_id,
      product_name,
      product_sku,
      quantity,
      unit_price,
      discount_id,       // applied line-item discount id (null if none)
      discount_name,     // display name of the applied discount
      discount_amount,
      cgst_amount,
      sgst_amount,
      igst_amount,
      tax_amount,
      line_total,
      salesperson_id,    // per-item salesperson (optional)
      salesperson_name,  // per-item salesperson display name (optional)
    }
  */

  // Totals — always from server response, never calculated on frontend
  gross_subtotal: '0.00',
  subtotal: '0.00',
  discount_total: '0.00',
  tax_total: '0.00',
  grand_total: '0.00',
  rounding_adjustment: '0.00',

  // Bill-level discount (from backend)
  bill_discount_id: null,
  bill_discount_name: null,
  bill_discount_amount: '0.00',
  bill_discount_type: null,

  // Coupon code discount (from backend PR #167) — legacy first/aggregate fields,
  // stay correct even for multi-coupon orders (see benefit-stacking-policy doc)
  coupon_code_id: null,
  coupon_code: null,
  coupon_discount_amount: '0.00',
  // Full applied-coupon list (benefit-stacking-policy) — [{ id, coupon_code_id, code, coupon_id, coupon_name, discount_amount, position }]
  coupons: [],

  // Engine-decided stacking outcome on the last mutation — { applied, blocked, clamped }
  stacking_trace: { applied: [], blocked: [], clamped: [] },

  // Promotions (from create/update order response)
  applied_promotions: [],          // [{ promotion_id, promotion_name, applicable_level, total_savings, affected_item_ids }]
  near_miss_hints: [],             // [{ promotion_id, promotion_name, hint_message, shortfall_type, shortfall_value }]
  bill_promotion_id: null,
  bill_promotion_name: null,
  bill_promotion_discount_amount: '0.00',
  total_promo_savings: '0.00',

  // Post-payment
  amount_paid: '0.00',
  change_due: '0.00',
  loyalty_points_earned: '0.00',

  // Loyalty redemption held on the draft/on-hold order (separate tender).
  // grand_total never changes; payable = grand_total − loyalty_redemption_amount.
  loyalty_points_redeemed: '0.00',
  loyalty_redemption_amount: '0.00',

  // Order notes
  notes: '',

  // UI state
  isLoading: false,
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    // Called after createOrder / updateOrder / holdOrder / cancelOrder API responses
    // Syncs the full order state from the backend response
    setCurrentOrder: (state, { payload }) => {
      const order = payload;
      state.currentOrderId = order.id;
      state.orderNumber = order.order_number;
      state.referenceNumber = order.reference_number;
      state.status = order.status;
      state.gross_subtotal = order.gross_subtotal;
      state.subtotal = order.subtotal;
      state.discount_total = order.discount_total;
      state.tax_total = order.tax_total;
      state.grand_total = order.grand_total;
      state.rounding_adjustment = order.rounding_adjustment || '0.00';
      state.amount_paid = order.amount_paid || '0.00';
      state.change_due = order.change_due || '0.00';
      state.loyalty_points_earned = order.loyalty_points_earned || '0.00';
      state.loyalty_points_redeemed = order.loyalty_points_redeemed || '0.00';
      state.loyalty_redemption_amount = order.loyalty_redemption_amount || '0.00';

      // Map backend items to cartItems shape
      state.cartItems = (order.items || []).map((item) => ({
        order_item_id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        product_sku: item.product_sku,
        quantity: parseFloat(item.quantity),
        unit_price: item.unit_price,
        discount_id: item.discount_id || null,
        discount_name: item.discount_name || null,
        discount_amount: item.discount_amount,
        promotion_id: item.promotion_id || null,
        promotion_name: item.promotion_name || null,
        promotion_discount_amount: item.promotion_discount_amount || '0.00',
        bill_promotion_discount_amount: item.bill_promotion_discount_amount || '0.00',
        total_discount_amount: item.total_discount_amount || '0.00',
        cgst_amount: item.cgst_amount,
        sgst_amount: item.sgst_amount,
        igst_amount: item.igst_amount,
        tax_amount: item.tax_amount,
        line_total: item.line_total,
        salesperson_id: item.salesperson_id || null,
        salesperson_name: item.salesperson_name || null,
      }));

      state.notes = order.notes || '';

      // Sync bill-level discount fields
      state.bill_discount_id     = order.bill_discount_id     || null;
      state.bill_discount_name   = order.bill_discount_name   || null;
      state.bill_discount_amount = order.bill_discount_amount || '0.00';
      state.bill_discount_type   = order.bill_discount_type   || null;

      // Sync coupon fields
      state.coupon_code_id         = order.coupon_code_id         || null;
      state.coupon_code            = order.coupon_code            || null;
      state.coupon_discount_amount = order.coupon_discount_amount || '0.00';
      state.coupons                = order.coupons                || [];

      // Sync stacking trace — always present on order responses (may be empty)
      state.stacking_trace = order.stacking_trace || { applied: [], blocked: [], clamped: [] };

      // Sync bill-level promotion fields
      state.bill_promotion_id             = order.bill_promotion_id             || null;
      state.bill_promotion_name           = order.bill_promotion_name           || null;
      state.bill_promotion_discount_amount= order.bill_promotion_discount_amount || '0.00';

      // Reconstruct applied_promotions from order data so resume-held-order shows
      // correct promo badge + billing rows (setAppliedPromotions overrides this on create/update)
      const linePromos = {};
      (order.items || []).forEach((item) => {
        if (item.promotion_id) {
          if (!linePromos[item.promotion_id]) {
            linePromos[item.promotion_id] = {
              promotion_id: item.promotion_id,
              promotion_name: item.promotion_name,
              applicable_level: 'line_item',
              total_savings: 0,
              affected_item_ids: [],
            };
          }
          linePromos[item.promotion_id].total_savings += parseFloat(item.promotion_discount_amount || 0);
          linePromos[item.promotion_id].affected_item_ids.push(item.id);
        }
      });
      const reconstructed = Object.values(linePromos).map((p) => ({
        ...p, total_savings: parseFloat(p.total_savings.toFixed(2)),
      }));
      if (order.bill_promotion_id) {
        reconstructed.push({
          promotion_id: order.bill_promotion_id,
          promotion_name: order.bill_promotion_name,
          applicable_level: 'bill',
          total_savings: parseFloat(order.bill_promotion_discount_amount || 0),
          affected_item_ids: (order.items || []).map((i) => i.id),
        });
      }
      state.applied_promotions = reconstructed;
      state.near_miss_hints    = [];
      state.total_promo_savings = reconstructed
        .reduce((sum, p) => sum + p.total_savings, 0)
        .toFixed(2);

      // Sync order-level customer.
      // Some endpoints may omit customer fields in partial payloads.
      // Only mutate selectedCustomer when customer_id key is explicitly present.
      if (Object.prototype.hasOwnProperty.call(order, 'customer_id')) {
        if (order.customer_id) {
          state.selectedCustomer = {
            id: order.customer_id,
            name: order.customer_name,
          };
        } else {
          state.selectedCustomer = null;
        }
      }

      // Sync order-level salesperson
      if (order.salesperson_id) {
        state.selectedSalesperson = {
          id: order.salesperson_id,
          name: order.salesperson_name,
        };
      } else {
        state.selectedSalesperson = null;
      }
    },

    // Set or change the customer attached to this order
    setSelectedCustomer: (state, { payload }) => {
      state.selectedCustomer = payload; // { id, name, phone_number, credit_balance }
    },

    // Clear customer selection
    clearCustomer: (state) => {
      state.selectedCustomer = null;
    },

    // Set order-level salesperson
    setOrderSalesperson: (state, { payload }) => {
      state.selectedSalesperson = payload; // { id, name } or null to clear
    },

    // Set per-item salesperson for a specific cart item
    // payload: { order_item_id, salesperson_id, salesperson_name }
    setItemSalesperson: (state, { payload }) => {
      const item = state.cartItems.find(
        (ci) => ci.order_item_id === payload.order_item_id
      );
      if (item) {
        item.salesperson_id = payload.salesperson_id;
        item.salesperson_name = payload.salesperson_name;
      }
    },

    // Called after every createOrder / updateOrder — stores promotion results from response
    setAppliedPromotions: (state, { payload }) => {
      const { applied_promotions = [], near_miss_hints = [] } = payload;
      state.applied_promotions  = applied_promotions;
      state.near_miss_hints     = near_miss_hints;
      // Sum ALL levels so bill-level promos (tracked separately in bill_promotion_discount_amount)
      // are included in the headline savings figure.
      state.total_promo_savings = applied_promotions
        .reduce((sum, p) => sum + (parseFloat(p.total_savings) || 0), 0)
        .toFixed(2);
    },

    // Set loading state while API call is in flight
    setOrderLoading: (state, { payload }) => {
      state.isLoading = payload;
    },

    // Reset everything — called after payment success or discard
    clearOrder: () => initialState,
  },
});

export const {
  setCurrentOrder,
  setSelectedCustomer,
  clearCustomer,
  setOrderSalesperson,
  setItemSalesperson,
  setAppliedPromotions,
  setOrderLoading,
  clearOrder,
} = orderSlice.actions;

export default orderSlice.reducer;
